import { Link } from 'react-router-dom';
import { getComic } from '@/mocks/comics';
import { getChapters } from '@/mocks/chapters';
import { formatViews } from '@/utils/format';

const heroImage =
  'https://readdy.ai/api/search-image?query=Korean%20webtoon%20manhwa%20wide%20banner%20illustration%2C%20panoramic%20lakeside%20camping%20scene%20at%20golden%20hour%2C%20a%20glowing%20orange%20tent%20beside%20calm%20water%20with%20pine%20forest%20silhouette%2C%20young%20couple%20silhouettes%20enjoying%20the%20sunset%20on%20a%20camping%20blanket%2C%20drifting%20fireflies%20and%20warm%20light%20particles%2C%20painterly%20anime%20style%20with%20rich%20warm%20orange%20and%20soft%20pink%20gradient%20sky%2C%20cinematic%20composition%2C%20no%20text&width=1600&height=900&seq=5177-hero&orientation=landscape';

const stats = [
  { value: '5000+', label: '部韩漫作品' },
  { value: '12万+', label: '话中文章节' },
  { value: '800万+', label: '在线读者' },
];

export default function Hero() {
  const comic = getComic('tent-secret');
  const chapters = comic ? getChapters(comic.id) : [];

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroImage} alt="5177漫画网免费韩国漫画阅读横幅" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/55 to-foreground-950/75" />
      </div>

      <div className="relative mx-auto flex w-full max-w-7xl flex-col gap-10 lg:flex-row lg:items-center justify-between px-4 md:px-6 py-16 md:py-20 lg:py-24">
        <div className="w-full lg:max-w-xl animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 border border-background-50/25 px-4 py-1.5 text-xs md:text-sm text-background-50 backdrop-blur-sm">
            <i className="ri-flashlight-line text-accent-300" aria-hidden="true" />
            全网免费 · 中文翻译 · 每日更新
          </span>

          <h1 className="mt-6 font-display text-4xl md:text-6xl leading-tight text-background-50">
            5177漫画网
          </h1>
          <p className="mt-4 text-base md:text-lg leading-7 md:leading-8 text-background-100">
            免费阅读《帐篷里的秘密》等热门韩国漫画，全集中文故事在线浏览，畅享免费又精彩的韩漫体验，随时沉浸其中。
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/comic/tent-secret"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-400"
            >
              <i className="ri-book-open-line text-base" aria-hidden="true" />
              立即免费阅读
            </Link>
            <Link
              to="/category/all"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-background-50/10 border border-background-50/30 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              <i className="ri-archive-2-line text-base" aria-hidden="true" />
              浏览漫画大全
            </Link>
          </div>

          <dl className="mt-10 flex items-center gap-8 md:gap-12">
            {stats.map((s) => (
              <div key={s.label}>
                <dt className="sr-only">{s.label}</dt>
                <dd className="font-display text-2xl md:text-3xl text-accent-300">{s.value}</dd>
                <dd className="mt-1 text-xs md:text-sm text-background-100">{s.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        {comic && (
          <div className="w-full lg:w-[340px] shrink-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <Link
              to={`/comic/${comic.id}`}
              className="group flex flex-col sm:flex-row lg:flex-col gap-4 rounded-lg bg-background-50/95 p-3 md:p-4 hover-lift"
              aria-label={`阅读《${comic.title}》漫画`}
            >
              <div className="relative w-28 sm:w-32 lg:w-full mx-auto lg:mx-0 shrink-0">
                <div className="relative aspect-[2/3] overflow-hidden rounded-md">
                  <img
                    src={comic.cover}
                    alt={`${comic.title}漫画封面`}
                    title={`${comic.title}漫画-免费阅读`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-1.5 top-1.5 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-medium text-foreground-950">
                    主编推荐
                  </span>
                </div>
              </div>
              <div className="flex-1 lg:mt-3 text-center lg:text-left">
                <h2 className="font-display text-lg md:text-xl text-foreground-950 group-hover:text-primary-600 transition-colors">
                  《{comic.title}》
                </h2>
                <p className="mt-1 text-xs text-foreground-600">
                  {comic.author} · {comic.originalTitle}
                </p>
                <div className="mt-2.5 flex items-center justify-center lg:justify-start gap-3 text-xs text-foreground-600">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                    {comic.rating.toFixed(1)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {formatViews(comic.views)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-book-2-line" aria-hidden="true" />
                    {chapters.length}话连载中
                  </span>
                </div>
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 whitespace-nowrap">
                  开始阅读
                  <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}